import { useDispatch, useSelector } from 'react-redux';
import { Buttons, FlexContainer, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { showModalAction } from 'Store/modules/app/actions';
import { phoneSelector } from 'Store/modules/auth/selectors';
import useTranslation from 'Hooks/useTranslation';
import { useFlags } from 'Hooks/useFlags';
import EnabledFlowInfo from 'Containers/Dashboard/components/EnabledFlowInfo';
import ResultInfo from 'Containers/Dashboard/components/TaxScreen/components/ResultInfo';
import { lottiesPaths } from 'Constants/lottiesPaths';
import { routes, routesByStage } from 'Constants/routes';
import { TAX_SEASON_OVER_MODAL } from 'Constants/modals';
import { openUrlInNewNavigator } from 'Utils/links';
import { WHATSAPP_NOT_PRESENTATION_URL } from 'Constants/whatsappLinks';
import { FlagsKey, TaxScreenType } from 'Types/common';
import * as Styled from '../styles';
import NewTaxButton from '../NewTaxButton';

const InProgress = ({ tax }: TaxScreenType): JSX.Element => {
  const { t } = useTranslation(locizeNS.DASHBOARD);
  const phoneSaved = useSelector(phoneSelector);
  const dispatch = useDispatch();
  const { enabled: flowEnabled } = useFlags(FlagsKey.FLOW_ENABLED);
  const { taxYear, enabled, taxReturnResult, filingInfo, isUserWithPreFiling } = tax;

  const handleOnClick = (): void => {
    if (flowEnabled) {
      dispatch(showModalAction({ options: { type: TAX_SEASON_OVER_MODAL } }));
    } else if (tax?.stage) {
      const route = !phoneSaved ? routes.PERSONAL_PHONE : routesByStage[tax?.stage];
      history.push(route);
    } else {
      const route = !phoneSaved ? routes.PERSONAL_PHONE : routes.PERSONAL_START;
      history.push(route);
    }
  };

  return (
    <>
      {!isUserWithPreFiling && <Styled.KiteLottie options={{ path: lottiesPaths.GREEN_KITE }} />}
      <Styled.InfoContainer
        justify="center"
        direction="column"
        align="center"
        gap="dimension_08"
        fullHeight
      >
        <Text size="L_L_S" color="dark" type="headline">
          {taxYear}
        </Text>
        <>
          <Text type="paragraphs" size="L_L_M" color="dark" align="center">
            {t('TaxScreen.InProgress.title', {
              context: (flowEnabled && 'flowEnabled') || (isUserWithPreFiling && 'prefilingUser'),
            })}
          </Text>
          <Styled.StyledLottie
            options={{
              path: isUserWithPreFiling
                ? lottiesPaths.NOT_MANDATORY
                : lottiesPaths.DASHBOARD_DATA_RETRIEVAL,
              rendererSetting: { progressiveLoad: true },
            }}
          />
          {(filingInfo || taxReturnResult) && isUserWithPreFiling && (
            <ResultInfo
              filingInfo={filingInfo}
              taxReturnResult={taxReturnResult}
              isUserWithPreFiling={isUserWithPreFiling}
            />
          )}
          {flowEnabled ? (
            <EnabledFlowInfo />
          ) : (
            <Styled.ButtonsWrapper gap="dimension_16" direction="row-reverse">
              {!isUserWithPreFiling ? (
                <>
                  <Buttons.Classic
                    size="m"
                    mode="primary"
                    onClick={handleOnClick}
                    disabled={!enabled}
                    testID="btnSubmit"
                    label={t('TaxScreen.InProgress.CTA')}
                  />
                  {enabled && <NewTaxButton flowEnabled={flowEnabled} />}
                </>
              ) : (
                <FlexContainer
                  direction="column"
                  align="center"
                  justify="center"
                  gap="dimension_16"
                >
                  <Text type="paragraphs" size="S_L_S" align="center" color="dark">
                    {t(`InProgress.prefilingUser.description`)}
                  </Text>
                  <Buttons.Tiny
                    label={t('TaxScreen.InProgress.tiny.prefilingUser')}
                    onClick={() => openUrlInNewNavigator(WHATSAPP_NOT_PRESENTATION_URL)}
                  />
                </FlexContainer>
              )}
            </Styled.ButtonsWrapper>
          )}
        </>
      </Styled.InfoContainer>
    </>
  );
};

export default InProgress;
